import React, { FC, useEffect, useMemo, useState } from 'react';
import { GridApi } from 'ag-grid-community';

import { Timezone } from '@/datetime/timezone.service';
import { LoadingFallback } from '@/main/LoadingFallback.atom';
import type { RangeExport } from '@/trendData/duration.store';
import { TreemapPropsOnly } from '@/treemap/TreemapPropsOnly.molecule';
import { SerializedRange, serializeRange } from '@/datetime/dateTime.utilities';
import { ColumnOrRowWithDefinitions, TreemapAsset, TreemapViewSettings } from '@/tableBuilder/tableBuilder.types';
import { getTreemapStructure } from '@/tableBuilder/tableBuilderTreemap.utilities';
import type { ConditionItem, ScalarItem, SeriesItem } from '@/trendData/trendData.types';

interface TableBuilderTreemapProps {
  conditions: ConditionItem[];
  displayRange: RangeExport;
  handleSwapAssets: (asset: TreemapAsset) => void;
  isPresentationMode: boolean;
  setTreemapViewSettings: (newSettings: TreemapViewSettings) => void;
  signals: (SeriesItem | ScalarItem)[];
  simpleColumns: ColumnOrRowWithDefinitions[];
  timezone: Timezone;
  treemapViewSettings: TreemapViewSettings;
  agGridApi?: GridApi | null;
}

export const TableBuilderTreemap: FC<TableBuilderTreemapProps> = ({
  agGridApi,
  conditions,
  displayRange,
  handleSwapAssets,
  isPresentationMode,
  setTreemapViewSettings,
  signals,
  simpleColumns,
  timezone,
  treemapViewSettings,
}) => {
  const { helpKey, breadcrumbs, parent, tree } = useMemo(
    () =>
      getTreemapStructure({
        agGridApi,
        conditions,
        simpleColumns,
        signals,
        currentSettings: treemapViewSettings,
        setTreemapViewSettings,
      }),
    [agGridApi, conditions, simpleColumns, signals, treemapViewSettings, setTreemapViewSettings],
  );

  const [serializedDisplayRange, setSerializedDisplayRange] = useState<SerializedRange>(serializeRange(displayRange));
  useEffect(() => setSerializedDisplayRange(serializeRange(displayRange)), [displayRange]);

  const onClickNode = (asset?: TreemapAsset, isLeafNode?: boolean) => {
    if (isLeafNode && asset) {
      handleSwapAssets(asset);
    } else {
      setTreemapViewSettings({ ...treemapViewSettings, parent: asset ?? treemapViewSettings.parent });
    }
  };

  return (
    <div className="height-maximum flex items-center justify-center">
      {!tree?.length ? (
        <LoadingFallback />
      ) : (
        <TreemapPropsOnly
          helpKey={helpKey}
          breadcrumbs={breadcrumbs}
          parent={parent}
          isContent={false}
          displayRange={serializedDisplayRange}
          timezone={timezone}
          rangeEditingEnabled={!isPresentationMode}
          onClickNode={onClickNode}
          tree={tree}
          isTableView
        />
      )}
    </div>
  );
};
